/**
 * הלקוחות — the Control Plane's list of customers. 09 §4.16, OA-0001 §3.9.
 *
 * A PARENT SCREEN on the PLATFORM surface: a list of rows of one entity kind, each with its own
 * address. It is not a context selector — the Control Plane has no company slot at all (09 §3.3:
 * a slot that does not apply is ABSENT, never greyed).
 *
 * Two rules from the accepted architecture are visible in the columns:
 *
 *   · A SUSPENDED CUSTOMER IS STILL LISTED. 08 §6.0 D11 makes `suspended` deny every action class,
 *     and commercial enforcement never deletes — so the row whose subscription grants nothing is
 *     precisely the row an operator has to be able to find in order to reinstate it.
 *   · THE COUNTS ARE THE SERVER'S. `companyCount` and `seatCount` are read, never recomputed here,
 *     and never filtered — 09 §5.1's "313 is not 313" is about a figure whose N nobody can name.
 *
 * The Control Plane sees METADATA: name, status, plan, counts, contacts. Never business data
 * (04 §13.2.1, `platform.tenant.read`). There is no column here that could carry any.
 */

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { DataTable } from '../components/DataTable';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { Column } from '../components/DataTable';
import { useCallback } from 'react';
import type { TenantAdminSummary } from '../api/contract';
import { subscriptionStateHe, tenantStatusHe } from '../api/adminVocabulary';
import { ServerRead } from '../components/ServerRead';
import { listOf, useServerData } from '../api/useServerData';
import { callOperation } from '../api/operations';
import { routes } from '../routes';

const TITLE_HE = 'הלקוחות של הפלטפורמה';

export function PlatformTenantsScreen(): JSX.Element {
  const ctx = useWorkContext();

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  // With `apps/api` running this list comes from `platform.tenant.read`. Until then it is labelled.
  /*
   * READ FROM THE SERVER. **Phase 2 §6, vertical E.**
   *
   * This is the entry point to every platform action in the product: every `tenantUid` that the
   * wired platform writes — suspend, close, reactivate, provision, the two offboarding transitions —
   * are performed against came from this list. While it was a fixture, an operator was choosing a
   * customer from development data and then issuing a real write against whatever uid that row
   * happened to carry.
   */
  const read = useCallback(() => callOperation('platform.tenant.read', {}), []);
  const tenantsRead = useServerData(read);
  const tenants: readonly TenantAdminSummary[] = listOf(tenantsRead.result, 'tenants');

  const columns: readonly Column<TenantAdminSummary>[] = [
    {
      key: 'name',
      headerHe: 'שם הלקוח',
      render: (t) => (
        <span className="company-cell">
          {/* 09 §9.1 V1: the legal name is the identity. The provisioning handle is a second line. */}
          <span className="company-cell__name">{t.legalName}</span>
          <span className="company-cell__id" dir="ltr">
            {t.slug}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      headerHe: 'מצב הלקוח',
      render: (t) => {
        const term = tenantStatusHe(t.status);
        return <StatusChip labelHe={term.labelHe} tone={term.tone} />;
      },
    },
    {
      key: 'subscription',
      headerHe: 'מצב המנוי',
      render: (t) => {
        const term = subscriptionStateHe(t.subscriptionState);
        return <StatusChip labelHe={term.labelHe} tone={term.tone} />;
      },
    },
    {
      key: 'companies',
      headerHe: 'חברות',
      numeric: true,
      render: (t) => <span dir="ltr">{t.companyCount}</span>,
    },
    {
      key: 'seats',
      headerHe: 'משתמשים',
      numeric: true,
      render: (t) => <span dir="ltr">{t.seatCount}</span>,
    },
  ];

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'קונסולת הפלטפורמה', href: routes.platformConsole }}
      provenanceHe="המספרים מוצגים כפי שנמדדו בצד השרת ואינם מחושבים מחדש במסך הזה."
    >
      <div className="actions-row">
        <a className="btn btn--primary" href={routes.platformTenantProvisioning}>
          הקמת לקוח חדש
        </a>
      </div>

      <ServerRead result={tenantsRead.result} whatHe="את רשימת הלקוחות" skeletonRows={8}>
        {(data) => (
          <DataTable<TenantAdminSummary>
            captionHe="לקוחות"
            columns={columns}
            rows={data.tenants}
            rowKey={(t) => t.tenantUid}
            childHref={(t) => routes.platformTenant(t.tenantUid)}
            filterSummaryHe={`מציג ${data.tenants.length} לקוחות — כל הלקוחות שהפלטפורמה מנהלת`}
            emptyStateHe="לא הוקמו עדיין לקוחות בפלטפורמה."
          />
        )}
      </ServerRead>
    </AppShell>
  );
}
